import { useState, useEffect, useRef, useCallback } from 'react';
import { BaseFormModal } from '@/components/BaseFormModal';
import { MultiSelect } from '@/components/MultiSelect';
import { Tooltip } from '@/components/Tooltip';
import { focusAfterPaint } from '@/utils/dom';

export interface PermissionGroup {
  id: number;
  title: string;
  [key: string]: any;
}

export interface AddUserSaveData {
  firstName: string;
  lastName: string;
  email: string;
  permissionGroupIds: number[];
  isLoginEnabled: boolean;
}

export interface AddUserModalProps {
  open: boolean;
  permissionGroups?: PermissionGroup[];
  theme?: string;
  saving?: boolean;
  onSave?: (data: AddUserSaveData) => void;
  onCancel: () => void;
}

const TOOLTIPS = {
  email:
    '<p>The user will be emailed a link to register if their login is enabled.</p>' +
    '<p>The same email address can be used for any number of users.</p>',
  accessGroups:
    '<p>These groups determine what the user can see and do on Orna.</p>' +
    '<p>In the <strong>Permissions</strong> section, you can update what these groups do as well as update which users have access to which groups.</p>' +
    '<p>User must have the <strong>\u201CMobile\u201D</strong> access group to show up on officer lists for Calls and Case Reports.</p>',
  loginEnabled:
    '<p><strong>Login Enabled</strong> - User can log in, user is billed, user is on officer lists.</p>' +
    '<p><strong>Login Disabled</strong> - User CANNOT log in, user is NOT billed, user is on officer lists.</p>' +
    '<p>User must have the <strong>\u201CMobile\u201D</strong> access group to show up on officer lists for Calls and Case Reports.</p>',
};

export function AddUserModal({
  open,
  permissionGroups = [],
  theme,
  saving = false,
  onSave,
  onCancel,
}: AddUserModalProps) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [selectedPermissionGroupIds, setSelectedPermissionGroupIds] = useState<number[]>([]);
  const [isLoginEnabled, setIsLoginEnabled] = useState(true);

  const firstNameRef = useRef<HTMLInputElement>(null);
  const lastNameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);

  // Reset form and focus first input when modal opens
  useEffect(() => {
    if (open) {
      setFirstName('');
      setLastName('');
      setEmail('');
      setSelectedPermissionGroupIds([]);
      setIsLoginEnabled(true);

      focusAfterPaint(firstNameRef);
    }
  }, [open]);

  const handleSave = useCallback(() => {
    if (!firstName || !firstName.trim()) {
      (window as any).App?.showPopupNotification?.('First name is required.', 'error');
      return;
    }

    if (!lastName || !lastName.trim()) {
      (window as any).App?.showPopupNotification?.('Last name is required.', 'error');
      return;
    }

    if (!email || !email.trim()) {
      (window as any).App?.showPopupNotification?.('Email is required.', 'error');
      return;
    }

    if (selectedPermissionGroupIds.length === 0) {
      (window as any).App?.showPopupNotification?.('At least one access group is required.', 'error');
      return;
    }

    onSave?.({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim(),
      permissionGroupIds: selectedPermissionGroupIds,
      isLoginEnabled,
    });
  }, [firstName, lastName, email, selectedPermissionGroupIds, isLoginEnabled, onSave]);

  return (
    <BaseFormModal
      open={open}
      title="Create New User"
      theme={theme}
      dialogClass="kstc-dialog--form kstc-dialog--wide"
      saveText={saving ? 'Saving...' : 'Create User'}
      saveDisabled={saving}
      busy={saving}
      onSave={handleSave}
      onCancel={onCancel}
    >
      <div className="kst-add-user-intro">
        <p className="kst-add-user-description">
          Add a new user to your organization. They will receive a registration email.
        </p>
        <p className="kst-add-user-required-note">
          <span className="kst-required-indicator">*</span> Required fields
        </p>
      </div>

      <div className="kstc-form-row">
        <div className="kstc-form-group kstc-form-group--half">
          <label className="kstc-label">
            First Name <span className="kst-required-indicator">*</span>
          </label>
          <input
            ref={firstNameRef}
            type="text"
            className="kstc-input kst-firstname-input"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="Enter first name"
            enterKeyHint="next"
            onKeyUp={(e) => {
              if (e.key === 'Enter') lastNameRef.current?.focus();
            }}
          />
        </div>
        <div className="kstc-form-group kstc-form-group--half">
          <label className="kstc-label">
            Last Name <span className="kst-required-indicator">*</span>
          </label>
          <input
            ref={lastNameRef}
            type="text"
            className="kstc-input"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            placeholder="Enter last name"
            enterKeyHint="next"
            onKeyUp={(e) => {
              if (e.key === 'Enter') emailRef.current?.focus();
            }}
          />
        </div>
      </div>

      <div className="kstc-form-group">
        <div className="kstc-label-row">
          <label className="kstc-label">
            Email <span className="kst-required-indicator">*</span>
          </label>
          <Tooltip
            content={TOOLTIPS.email}
            position="top"
            maxWidth="320px"
            theme={theme}
          />
        </div>
        <input
          ref={emailRef}
          type="email"
          className="kstc-input"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Enter email address"
          enterKeyHint="done"
          onKeyUp={(e) => {
            if (e.key === 'Enter') emailRef.current?.blur();
          }}
        />
      </div>

      <div className="kstc-form-group">
        <div className="kstc-label-row">
          <label className="kstc-label">
            Access Group(s) <span className="kst-required-indicator">*</span>
          </label>
          <Tooltip
            content={TOOLTIPS.accessGroups}
            position="top"
            maxWidth="360px"
            theme={theme}
          />
        </div>
        <MultiSelect
          value={selectedPermissionGroupIds}
          items={permissionGroups}
          labelKey="title"
          valueKey="id"
          placeholder="Select access groups..."
          onChange={setSelectedPermissionGroupIds}
        />
      </div>

      <div className="kst-add-user-toggle-section">
        <div className="kst-toggle-row">
          <div className="kst-toggle-label">
            <span>Login Enabled</span>
            <Tooltip
              content={TOOLTIPS.loginEnabled}
              position="top"
              maxWidth="380px"
              theme={theme}
            />
          </div>
          <label className="kst-toggle">
            <input
              type="checkbox"
              className="kst-toggle-input"
              checked={isLoginEnabled}
              onChange={(e) => setIsLoginEnabled(e.target.checked)}
            />
            <span className="kst-toggle-slider"></span>
          </label>
        </div>
        <p className="kst-toggle-hint">
          {isLoginEnabled
            ? 'User can log in and will be billed.'
            : 'User cannot log in and will not be billed.'}
        </p>
      </div>
    </BaseFormModal>
  );
}

(window as any).AddUserModalReact = AddUserModal;
